import type { TipStep } from './types';

export type Rect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export type CardPos = { top: number; left: number };

export const TIP_PAD = 16;
export const TIP_GAP = 12;
export const TIP_STICKY = 72;
/** Breathing room around compact chrome (logo, nav chips, tips). */
export const TIP_CHROME_PAD = 6;

const maxSpotHeight = (viewportHeight: number) =>
  Math.min(280, Math.round(viewportHeight * 0.38));

const clampIntoViewport = (rect: Rect, vw: number, vh: number): Rect => {
  let { top, left, width, height } = rect;
  if (left < 0) {
    width += left;
    left = 0;
  }
  if (top < 0) {
    height += top;
    top = 0;
  }
  if (left + width > vw) width = Math.max(1, vw - left);
  if (top + height > vh) height = Math.max(1, vh - top);
  return {
    top,
    left,
    width: Math.max(1, width),
    height: Math.max(1, height),
  };
};

/**
 * Compact top-bar controls must be framed tightly. Inflating to a 40×72
 * minimum shifts the hole down/right of small icons (logo, tips, settings).
 */
export const isStickyChromeTarget = (raw: Rect): boolean => {
  const stickyFloor = TIP_STICKY + 8;
  return raw.top < stickyFloor && raw.top + raw.height <= stickyFloor + 8;
};

/** Clamp a target rect into a clear, on-screen spotlight band. */
export const clampSpotlight = (
  raw: Rect,
  viewport: { width: number; height: number } = {
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  }
): Rect => {
  const vw = viewport.width;
  const vh = viewport.height;
  const maxH = maxSpotHeight(vh);
  const stickyFloor = TIP_STICKY + 8;

  if (isStickyChromeTarget(raw)) {
    return clampIntoViewport(
      {
        top: raw.top - TIP_CHROME_PAD,
        left: raw.left - TIP_CHROME_PAD,
        width: raw.width + TIP_CHROME_PAD * 2,
        height: raw.height + TIP_CHROME_PAD * 2,
      },
      vw,
      vh
    );
  }

  // Page content — keep a readable band under the sticky bar for tall surfaces.
  const left = Math.max(0, Math.min(raw.left, vw - TIP_PAD));
  const width = Math.max(
    1,
    Math.min(raw.width, vw - left - TIP_PAD, raw.left + raw.width - left)
  );

  const visibleTop = Math.max(raw.top, stickyFloor);
  const visibleBottom = Math.min(raw.top + raw.height, vh - TIP_PAD);
  const visibleH = Math.max(0, visibleBottom - visibleTop);

  let top = visibleTop;
  let height = Math.min(visibleH || Math.min(raw.height, maxH), maxH);

  // Zero-size / missing paint: keep a small fallback without shifting away
  // from the measured origin (no 40×72 inflation).
  if (height < 1 || width < 1) {
    return clampIntoViewport(
      {
        top: Math.max(0, raw.top),
        left: Math.max(0, raw.left),
        width: Math.max(raw.width, 24),
        height: Math.max(raw.height, 24),
      },
      vw,
      vh
    );
  }

  if (top + height > vh - TIP_PAD) {
    height = Math.max(1, vh - TIP_PAD - top);
  }

  return { top, left, width, height };
};

/** Place the tip card in free viewport space, never covering the spot. */
export const placeTipCard = (
  spot: Rect,
  cardW: number,
  cardH: number,
  prefer: TipStep['placement'],
  viewport: { width: number; height: number } = {
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  }
): CardPos => {
  const vw = viewport.width;
  const vh = viewport.height;
  const w = Math.min(cardW, vw - TIP_PAD * 2);
  const h = Math.min(cardH, vh - TIP_PAD * 2);

  const fits = (top: number, left: number) => {
    if (top < TIP_PAD || left < TIP_PAD) return false;
    if (top + h > vh - TIP_PAD || left + w > vw - TIP_PAD) return false;
    const s = {
      top: spot.top - TIP_GAP,
      left: spot.left - TIP_GAP,
      right: spot.left + spot.width + TIP_GAP,
      bottom: spot.top + spot.height + TIP_GAP,
    };
    const c = { top, left, right: left + w, bottom: top + h };
    const overlap = !(
      c.right <= s.left ||
      c.left >= s.right ||
      c.bottom <= s.top ||
      c.top >= s.bottom
    );
    return !overlap;
  };

  const candidates: CardPos[] = [];
  const preferOrder: Array<TipStep['placement']> = [
    prefer && prefer !== 'auto' ? prefer : 'bottom',
    'bottom',
    'top',
    'right',
    'left',
  ];

  for (const p of preferOrder) {
    if (p === 'bottom') {
      candidates.push({
        top: spot.top + spot.height + TIP_GAP,
        left: Math.min(Math.max(TIP_PAD, spot.left), vw - w - TIP_PAD),
      });
    } else if (p === 'top') {
      candidates.push({
        top: spot.top - h - TIP_GAP,
        left: Math.min(Math.max(TIP_PAD, spot.left), vw - w - TIP_PAD),
      });
    } else if (p === 'right') {
      candidates.push({
        top: Math.min(Math.max(TIP_PAD, spot.top), vh - h - TIP_PAD),
        left: spot.left + spot.width + TIP_GAP,
      });
    } else if (p === 'left') {
      candidates.push({
        top: Math.min(Math.max(TIP_PAD, spot.top), vh - h - TIP_PAD),
        left: spot.left - w - TIP_GAP,
      });
    }
  }

  candidates.push(
    { top: vh - h - TIP_PAD, left: vw - w - TIP_PAD },
    { top: TIP_STICKY + TIP_PAD, left: vw - w - TIP_PAD },
    { top: vh - h - TIP_PAD, left: TIP_PAD },
    { top: TIP_STICKY + TIP_PAD, left: TIP_PAD }
  );

  for (const c of candidates) {
    const top = Math.min(Math.max(TIP_PAD, c.top), vh - h - TIP_PAD);
    const left = Math.min(Math.max(TIP_PAD, c.left), vw - w - TIP_PAD);
    if (fits(top, left)) return { top, left };
  }

  return {
    top: Math.max(TIP_PAD, vh - h - TIP_PAD),
    left: Math.max(TIP_PAD, vw - w - TIP_PAD),
  };
};
